import { useStore } from "@/store";
import { useCallback } from "react";
import type { ViewMode } from "@/store/slices/uiSlice";

export interface UIState {
  // Tool selection
  selectedTool: string;
  selectedFocus: string;

  viewMode: ViewMode;

  // Context Sidebar states
  contextSidebarDismissedDomains: string[];

  // Actions
  setSelectedTool: (tool: string) => void;
  setSelectedFocus: (focus: string) => void;
  resetHeaderTools: () => void;
  dismissContextSidebarForDomain: (domainId: string) => void;
  isContextSidebarDismissedForDomain: (domainId: string) => boolean;
  hydrateViewMode: (userId: string | null) => void;
  setViewMode: (userId: string | null, mode: ViewMode) => void;
  resetSessionState: () => void;

  // Helper functions
  hasPersonaOrContextSelected: () => boolean;
  hasPersonaSelected: () => boolean;
  hasCurrentUrl: () => boolean;
  currentUrl: string;
  shouldShowContextSidebar: (domainId?: string) => boolean;

}

/**
 * Custom hook to access and manage UI-related state and operations
 */
export const useUI = (): UIState => {
  const {
    // Header tools state
    headerTools,

    viewMode,

    // Context Sidebar states
    contextSidebarDismissedDomains,

    // Actions
    setSelectedTool,
    setSelectedFocus,
    resetHeaderTools,
    dismissContextSidebarForDomain,
    isContextSidebarDismissedForDomain,
    hydrateViewMode,
    setViewMode,
    resetSessionState,

    // For URL display
    currentPage,

    // Get personas, offers, and policies for generation state checking
    personas,
    offers,
    policies,

    userData,
    isUserLoading,
  } = useStore();

  // Helper function to check if any persona or offer is selected
  const hasPersonaOrContextSelected = () => {
    // Check if any of the selected personas are completed (not pending)
    const hasCompletedPersonas = headerTools.selectedPersonas.some(
      (selectedPersonaId) => {
        const persona = personas.find((p) => p.id === selectedPersonaId);
        // Persona is completed if it's not pending (or has no status, which means manually added)
        return persona && persona.status !== "pending";
      },
    );

    // Check if any of the selected offers are completed (not pending)
    const hasCompletedOffers = headerTools.selectedOffers.some(
      (selectedOfferId) => {
        const offer = offers.find((o) => o.id === selectedOfferId);
        // Offer is completed if it's not pending (or has no status, which means manually added)
        return offer && offer.status !== "pending";
      },
    );

    return hasCompletedPersonas || hasCompletedOffers;
  };

  // Helper function to check if any persona is selected
  const hasPersonaSelected = (): boolean => {
    // Don't evaluate until user data is loaded
    if (isUserLoading || !userData) {
      return false;
    }

    // If user has no domain, they can't have personas
    if (!userData.currentDomainId) {
      return false;
    }

    // Check if any of the selected personas are completed (not pending)
    // This allows manually added personas to enable the button even if generated ones are still pending
    const hasCompletedPersonas = headerTools.selectedPersonas.some(
      (selectedPersonaId) => {
        const persona = personas.find((p) => p.id === selectedPersonaId);
        // Persona is completed if it's not pending (or has no status, which means manually added)
        return persona && persona.status !== "pending";
      },
    );

    return hasCompletedPersonas;
  };

  const currentUrl = currentPage?.url || "";

  // Helper function to check if currentUrl is set
  const hasCurrentUrl = () => {
    return currentUrl.trim() !== "";
  };

  // Helper function to determine if context sidebar should auto-expand
  const shouldShowContextSidebar = useCallback(
    (domainId?: string) => {
      // Don't show if already dismissed for this domain
      if (domainId && isContextSidebarDismissedForDomain(domainId)) {
        return false;
      }

      // Show sidebar when workspace is fresh (no personas, offers, or policies)
      const hasNoPersonas = personas.length === 0;
      const hasNoOffers = offers.length === 0;
      const hasNoPolicies = policies.length === 0;

      return hasNoPersonas && hasNoOffers && hasNoPolicies;
    },
    [
      isContextSidebarDismissedForDomain,
      personas.length,
      offers.length,
      policies.length,
    ],
  );

  return {
    // State
    selectedTool: headerTools.selectedTool,
    selectedFocus: headerTools.selectedFocus,

    viewMode,

    // Context Sidebar states
    contextSidebarDismissedDomains,

    // Actions
    setSelectedTool,
    setSelectedFocus,
    resetHeaderTools,
    dismissContextSidebarForDomain,
    isContextSidebarDismissedForDomain,
    hydrateViewMode,
    setViewMode,
    resetSessionState,

    // Helper functions
    hasPersonaOrContextSelected,
    hasPersonaSelected,
    hasCurrentUrl,
    currentUrl,
    shouldShowContextSidebar,

  };
};
